import { Box, Card, CardContent, Chip, CircularProgress, Grid, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

// Shared palette (kept in sync with ClinicLocationStatsDrawer for a consistent Stats look).
export const INSIGHT_COLORS = [
  '#7450AA',
  '#FF5F35',
  '#34EBB7',
  '#418571',
  '#9B59B6',
  '#FFA500',
  '#2D9CDB',
  '#EB5757',
]

// Many top-K keys are slugs (e.g. "nutrition-consultation", "session_focus_sleep"). Prettify for display.
export function prettifyKey(key) {
  if (key === null || key === undefined) return '—'
  const str = String(key).trim()
  if (!str) return '—'
  // Leave human strings (with spaces / capitals) mostly intact; only normalise slug separators.
  if (/[\s]/.test(str) || /[a-z].*[A-Z]/.test(str)) return str
  return str
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Titled surface used for each insight block. */
export function InsightsCard({ title, subtitle, children }) {
  return (
    <Card sx={{ boxShadow: 2, height: '100%', borderRadius: '12px' }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          {title}
        </Typography>
        {subtitle ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            {subtitle}
          </Typography>
        ) : null}
        {children}
      </CardContent>
    </Card>
  )
}

/** A compact summary number tile. */
export function StatTile({ label, value, color = '#7450AA' }) {
  return (
    <Card sx={{ height: '100%', color: 'white', borderRadius: '12px', background: color }}>
      <CardContent sx={{ py: 2 }}>
        <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
          {label}
        </Typography>
        <Typography variant="h4" fontWeight="bold">
          {value ?? 0}
        </Typography>
      </CardContent>
    </Card>
  )
}

/** A 0–1 rate rendered as a percentage pill with a label. Shows "—" when the value is absent. */
export function RatePill({ label, value }) {
  const hasValue = value !== null && value !== undefined && !Number.isNaN(Number(value))
  const pct = Math.round((Number(value) || 0) * 100)
  return (
    <Box
      sx={{
        border: '1px solid rgba(116, 80, 170, 0.25)',
        borderRadius: '10px',
        px: 2,
        py: 1.5,
        backgroundColor: 'rgba(116, 80, 170, 0.05)',
        minWidth: 120,
        flex: '1 1 140px',
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color: '#7450AA' }}>
        {hasValue ? `${pct}%` : '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

/** A titled card holding a flex-wrap of rate pills. rates: [{ label, value }] (value 0..1 or null). */
export function RateGroup({ title, subtitle, rates }) {
  return (
    <InsightsCard title={title} subtitle={subtitle}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
        {rates.map((r) => (
          <RatePill key={r.label} label={r.label} value={r.value} />
        ))}
      </Box>
    </InsightsCard>
  )
}

/** A compact "average" tile (non-integer metric, e.g. avg medications / consultation). */
export function MetricTile({ label, value, suffix }) {
  return (
    <Box
      sx={{
        border: '1px solid rgba(45, 156, 219, 0.3)',
        borderRadius: '10px',
        px: 2,
        py: 1.5,
        backgroundColor: 'rgba(45, 156, 219, 0.06)',
        minWidth: 140,
        flex: '1 1 160px',
      }}
    >
      <Typography variant="h5" fontWeight={700} sx={{ color: '#2D9CDB' }}>
        {value ?? 0}
        {suffix ? <Typography component="span" variant="body2" sx={{ ml: 0.5 }}>{suffix}</Typography> : null}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

/**
 * Donut showing the %-split of a [{key,count}] list. When `total` (grand total across ALL items, not
 * just the returned top-K) exceeds the visible sum, an "Others" slice is added so the percentages
 * reflect the true split.
 */
export function DonutChart({ items, total, emptyLabel }) {
  if (!items || items.length === 0) {
    return <SectionEmpty label={emptyLabel} />
  }
  const visibleSum = items.reduce((acc, it) => acc + (Number(it?.count) || 0), 0)
  const data = items.map((it) => ({ name: prettifyKey(it?.key), value: Number(it?.count) || 0 }))
  if (total && total > visibleSum) {
    data.push({ name: 'Others', value: total - visibleSum })
  }
  return (
    <ResponsiveContainer width="100%" height={280}>
      <PieChart>
        <Pie
          data={data}
          dataKey="value"
          nameKey="name"
          cx="50%"
          cy="50%"
          innerRadius={55}
          outerRadius={90}
          paddingAngle={1}
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
          labelLine={false}
        >
          {data.map((entry, index) => (
            <Cell key={`d-${index}`} fill={INSIGHT_COLORS[index % INSIGHT_COLORS.length]} />
          ))}
        </Pie>
        <Tooltip />
        <Legend />
      </PieChart>
    </ResponsiveContainer>
  )
}

export function SectionEmpty({ label = 'No data for this period' }) {
  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
    </Box>
  )
}

export function InsightsLoading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 320 }}>
      <CircularProgress />
    </Box>
  )
}

/**
 * Horizontal "top N" bar list for [{ key, count }] items. Each row is labelled with the prettified
 * key and annotated with its count.
 */
export function TopKBarChart({ items, color = '#7450AA', emptyLabel }) {
  if (!items || items.length === 0) {
    return <SectionEmpty label={emptyLabel} />
  }
  const data = items.map((it) => ({ label: prettifyKey(it?.key), count: Number(it?.count) || 0 }))
  const height = Math.max(120, data.length * 40)
  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 36, bottom: 4, left: 8 }}>
        <XAxis type="number" hide allowDecimals={false} />
        <YAxis
          type="category"
          dataKey="label"
          width={150}
          tick={{ fontSize: 12, fill: '#475569' }}
          interval={0}
        />
        <Tooltip
          cursor={{ fill: 'rgba(116, 80, 170, 0.06)' }}
          contentStyle={{ borderRadius: 8, border: '1px solid #e0e0e0' }}
        />
        <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={20}>
          {data.map((entry, index) => (
            <Cell key={`c-${index}`} fill={color} />
          ))}
          <LabelList dataKey="count" position="right" style={{ fill: '#334155', fontSize: 12 }} />
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  )
}

/** Grid of StatTiles from a [{label, value, color}] spec. */
export function StatTileRow({ tiles }) {
  return (
    <Grid container spacing={2}>
      {tiles.map((t, i) => (
        <Grid item xs={6} sm={3} key={t.label}>
          <StatTile label={t.label} value={t.value} color={t.color || INSIGHT_COLORS[i % INSIGHT_COLORS.length]} />
        </Grid>
      ))}
    </Grid>
  )
}

// ---- Shared time-period selector (one component reused across the My Reports sub-tabs) ----

export const RANGE_PRESETS = [
  { id: 'last7', label: 'Last 7 days', days: 7 },
  { id: 'last30', label: 'Last 30 days', days: 30 },
  { id: 'last90', label: 'Last 90 days', days: 90 },
  { id: 'thisMonth', label: 'This month' },
]

/** Maps a preset id to { from, to } ISO LocalDate strings (the backend takes java.time.LocalDate). */
export function computeRange(presetId) {
  const to = dayjs()
  if (presetId === 'thisMonth') {
    return { from: to.startOf('month').format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD') }
  }
  const preset = RANGE_PRESETS.find((p) => p.id === presetId) || RANGE_PRESETS[1]
  return {
    from: to.subtract(preset.days - 1, 'day').format('YYYY-MM-DD'),
    to: to.format('YYYY-MM-DD'),
  }
}

/** Chip-based time-period selector. Controlled via `value` (preset id) + `onChange(id)`. */
export function ReportTimePeriodSelector({ value, onChange }) {
  return (
    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, mr: 0.5 }}>
        Period
      </Typography>
      {RANGE_PRESETS.map((preset) => {
        const selected = value === preset.id
        return (
          <Chip
            key={preset.id}
            label={preset.label}
            size="small"
            onClick={() => onChange(preset.id)}
            sx={{
              cursor: 'pointer',
              fontWeight: selected ? 600 : 400,
              backgroundColor: selected ? 'rgba(116, 80, 170, 0.15)' : 'background.paper',
              color: selected ? '#7450AA' : 'text.secondary',
              border: '1px solid',
              borderColor: selected ? '#7450AA' : 'divider',
            }}
          />
        )
      })}
    </Stack>
  )
}
